import React, { useMemo } from 'react';
import { Button, List, Space, Result, Alert, Spin } from 'antd';
import { WarningFilled } from '@ant-design/icons';
import { ReactPlugin, useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { useVirtualList } from 'ahooks';
import { browserName, isMobile } from 'react-device-detect';
import { useBlobStorage, IBlob } from '../../../hooks/useBlobStorage';

const errorMessage: string = `We’re sorry, but your request can’t be completed at this time.
  We’re working to resolve the issue as quickly as possible. Please try again later. Thank you.`;

const partialErrorMessage: string = `We’re sorry but there’s a delay in loading Machine-Readable Files at the moment.
  We’re working to resolve the issue as quickly as possible. Please try again later. Thank you.`;

const FileListComponent: React.FC = () => {
  const { isLoading, blobList, isError, isPartialError, error, refetch } = useBlobStorage();
  const appInsights: ReactPlugin = useAppInsightsContext();

  // Memoize the blob list to avoid unnecessary re-renders
  const memoizedBlobList = useMemo(() => blobList || [], [blobList]);

  // Debounce the blob list to reduce flickering during rapid updates
  const [debouncedBlobList, setDebouncedBlobList] = React.useState(memoizedBlobList);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedBlobList(memoizedBlobList);
    }, 100); // 100ms debounce

    return () => clearTimeout(timer);
  }, [memoizedBlobList]);

  // Remove debug logging to reduce effect calls

  // Use virtualization for desktop browsers, fallback to pagination for mobile
  const shouldUseVirtualization = useMemo(() => {
    // Disable virtualization on mobile due to touch scrolling conflicts
    return !isMobile && debouncedBlobList.length > 1000;
  }, [debouncedBlobList.length]);

  // For mobile: implement simple pagination
  const itemsPerPage = 50;
  const [currentPage, setCurrentPage] = React.useState(1);
  const paginatedList = useMemo(() => {
    if (shouldUseVirtualization || isMobile === false) return debouncedBlobList;
    const startIndex = (currentPage - 1) * itemsPerPage;
    return debouncedBlobList.slice(startIndex, startIndex + itemsPerPage);
  }, [debouncedBlobList, currentPage, shouldUseVirtualization]);

  // Reset to first page when data changes significantly (search)
  React.useEffect(() => {
    if (isMobile) setCurrentPage(1);
  }, [debouncedBlobList.length]);

  // Virtual list configuration for all browsers
  const containerRef = React.useRef<HTMLDivElement>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const [virtualList, scrollTo] = useVirtualList(debouncedBlobList, {
    containerTarget: containerRef,
    wrapperTarget: wrapperRef,
    itemHeight: 56, // Approximate height of each list item
    overscan: 10, // Render extra items outside the visible area for smooth scrolling
  });

  // Simple scroll reset only on significant search changes (less aggressive)
  const prevListLengthRef = React.useRef(debouncedBlobList.length);
  React.useEffect(() => {
    const currentLength = debouncedBlobList.length;
    const prevLength = prevListLengthRef.current;

    // Only scroll to top if list dramatically decreased (likely a search)
    if (scrollTo && currentLength < prevLength / 2 && currentLength < 1000) {
      scrollTo(0); // Remove setTimeout to prevent additional render cycles
    }

    prevListLengthRef.current = currentLength;
  }, [debouncedBlobList.length, scrollTo]);

  // Memoize the onClick handler to prevent unnecessary re-renders
  const onFileClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement>, blob: IBlob): void => {
      // When a MRF link is clicked (downloaded), track the event
      appInsights.trackEvent({ name: 'File Downloaded', properties: { file: blob.name } });
    },
    [appInsights]
  );

  // Track analytics only when significant state changes occur (not on every render)
  React.useEffect(() => {
    appInsights.trackEvent({
      name: 'FileList Render',
      properties: {
        isLoading,
        blobListLength: blobList?.length,
        isPartialError,
        isError,
        error: error?.toString(),
        browserName,
        usingVirtualization: shouldUseVirtualization,
        isMobile,
      },
    });
  }, [isLoading, blobList?.length, isPartialError, isError, appInsights, browserName]); // Add dependencies

  if (isError) {
    // In the event no MRFs are available due to errors, render an error message
    return (
      <Result
        status='error'
        title='Error fetching files. Please try again soon.'
        subTitle={errorMessage}
        extra={
          <Button type='primary' loading={isLoading} onClick={refetch} data-testid='file-fetching-error'>
            Try Again
          </Button>
        }
      />
    );
  }

  return (
    <>
      {/* In the event some MRFs are available, but not all, render an error message along with the list of available MRFs */}
      {isPartialError && (
        <Alert banner description={partialErrorMessage} type='warning' showIcon icon={<WarningFilled />} />
      )}

      {shouldUseVirtualization ? (
        /* Virtualized list container for desktop browsers */
        <div
          ref={containerRef}
          style={{
            height: '600px', // Fixed height container for virtualization
            overflowY: 'auto',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
          }}
          data-testid='list'
        >
          <div ref={wrapperRef}>
            {isLoading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Spin size='large' tip='Loading files...' />
              </div>
            ) : (
              virtualList.map((item) => (
                <div
                  key={item.index}
                  style={{
                    height: 56,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 24px',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                  data-testid={`list-item-${item.index}`}
                >
                  <Space>
                    <span>{item.index + 1}.</span>
                    <a
                      href={item.data.downloadUrl}
                      onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => onFileClick(e, item.data)}
                      style={{ wordBreak: 'break-word' }}
                    >
                      {item.data.name}
                    </a>
                  </Space>
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        /* Mobile-friendly regular list with pagination */
        <>
          <List
            data-testid='list'
            loading={isLoading}
            size='default'
            dataSource={paginatedList}
            renderItem={(blob: IBlob, index: number) => (
              <List.Item data-testid={`list-item-${index}`}>
                <Space>
                  <span>{(currentPage - 1) * itemsPerPage + index + 1}.</span>
                  <a
                    href={blob.downloadUrl}
                    onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => onFileClick(e, blob)}
                    style={{ wordBreak: 'break-word' }}
                  >
                    {blob.name}
                  </a>
                </Space>
              </List.Item>
            )}
          />
          {/* Pagination for mobile */}
          {isMobile && debouncedBlobList.length > itemsPerPage && (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <Button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
                style={{ marginRight: '10px' }}
              >
                Previous
              </Button>
              <span>
                Page {currentPage} of {Math.ceil(debouncedBlobList.length / itemsPerPage)}
              </span>
              <Button
                disabled={currentPage >= Math.ceil(debouncedBlobList.length / itemsPerPage)}
                onClick={() => setCurrentPage((prev) => prev + 1)}
                style={{ marginLeft: '10px' }}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
};

// Export memoized component to prevent unnecessary re-renders
export const FileList = React.memo(FileListComponent);
